import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const useStyles = makeStyles((theme) => ({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 320,
  },
  btnWrapper: {
    position: "relative",
  },
}));

const getRegionFromConnection = (connection) =>
  connection?.emailImapHost?.includes(".zoho.eu") ? "eu" : "com";

const buildInitialValues = (connection) => {
  if (!connection) {
    return {
      name: "",
      emailAddress: "",
      emailAuthUser: "",
      emailAuthPass: "",
      region: "com",
      emailSmtpPort: 465,
      queueIds: [],
    };
  }

  return {
    name: connection.name || "",
    emailAddress: connection.emailAddress || connection.number || "",
    emailAuthUser: connection.emailAuthUser || connection.emailAddress || "",
    emailAuthPass: "",
    region: getRegionFromConnection(connection),
    emailSmtpPort: connection.emailSmtpPort || 465,
    queueIds: (connection.queues || []).map((q) => q.id),
  };
};

const ZohoMailModal = ({ open, onClose, connection }) => {
  const classes = useStyles();
  const [showPassword, setShowPassword] = useState(false);
  const [testing, setTesting] = useState(false);
  const isEdit = Boolean(connection?.id);

  const initialValues = useMemo(
    () => buildInitialValues(connection),
    [connection]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().min(2).required(),
        emailAddress: Yup.string().email().required(),
        emailAuthPass: isEdit
          ? Yup.string().min(4).nullable()
          : Yup.string().min(4).required(),
      }),
    [isEdit]
  );

  const handleTest = async (values) => {
    if (!values.emailAuthPass) {
      toast.info(i18n.t("connections.zohoModal.passwordRequiredForTest"));
      return;
    }

    setTesting(true);
    try {
      await api.post("/email/test", {
        emailAddress: values.emailAddress,
        emailAuthUser: values.emailAuthUser || values.emailAddress,
        emailAuthPass: values.emailAuthPass,
        region: values.region,
        emailSmtpPort: values.emailSmtpPort,
      });
      toast.success(i18n.t("connections.zohoModal.testSuccess"));
    } catch (err) {
      toastError(err);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (values, { setSubmitting }) => {
    try {
      const payload = {
        name: values.name,
        emailAuthUser: values.emailAuthUser || values.emailAddress,
        region: values.region,
        emailSmtpPort: values.emailSmtpPort,
        queueIds: values.queueIds,
      };

      if (values.emailAuthPass) {
        payload.emailAuthPass = values.emailAuthPass;
      }

      if (isEdit) {
        await api.put(`/email/${connection.id}`, payload);
        toast.success(i18n.t("connections.zohoModal.updateSuccess"));
      } else {
        await api.post("/email/", {
          ...payload,
          emailAddress: values.emailAddress,
          emailAuthPass: values.emailAuthPass,
        });
        toast.success(i18n.t("connections.zohoModal.success"));
      }

      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {isEdit
          ? i18n.t("connections.zohoModal.editTitle")
          : i18n.t("connections.zohoModal.title")}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSave}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form className={classes.form}>
            <DialogContent dividers>
              <Field
                as={TextField}
                label={i18n.t("connections.zohoModal.name")}
                name="name"
                fullWidth
                variant="outlined"
                margin="dense"
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
              <Field
                as={TextField}
                label={i18n.t("connections.zohoModal.email")}
                name="emailAddress"
                fullWidth
                variant="outlined"
                margin="dense"
                disabled={isEdit}
                error={touched.emailAddress && Boolean(errors.emailAddress)}
                helperText={
                  (touched.emailAddress && errors.emailAddress) ||
                  (isEdit ? i18n.t("connections.zohoModal.emailLocked") : "")
                }
              />
              <Field
                as={TextField}
                label={i18n.t("connections.zohoModal.authUser")}
                name="emailAuthUser"
                fullWidth
                variant="outlined"
                margin="dense"
                placeholder={values.emailAddress}
                helperText={i18n.t("connections.zohoModal.authUserHint")}
              />
              <Field
                as={TextField}
                label={i18n.t("connections.zohoModal.appPassword")}
                name="emailAuthPass"
                type={showPassword ? "text" : "password"}
                fullWidth
                variant="outlined"
                margin="dense"
                error={touched.emailAuthPass && Boolean(errors.emailAuthPass)}
                helperText={
                  (touched.emailAuthPass && errors.emailAuthPass) ||
                  (isEdit
                    ? i18n.t("connections.zohoModal.appPasswordEditHint")
                    : i18n.t("connections.zohoModal.appPasswordHint"))
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>{i18n.t("connections.zohoModal.region")}</InputLabel>
                <Field
                  as={Select}
                  label={i18n.t("connections.zohoModal.region")}
                  name="region"
                >
                  <MenuItem value="com">Zoho.com (Américas / Global)</MenuItem>
                  <MenuItem value="eu">Zoho.eu (Europa)</MenuItem>
                </Field>
              </FormControl>
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>{i18n.t("connections.zohoModal.smtpPort")}</InputLabel>
                <Field
                  as={Select}
                  label={i18n.t("connections.zohoModal.smtpPort")}
                  name="emailSmtpPort"
                >
                  <MenuItem value={465}>465 (SSL)</MenuItem>
                  <MenuItem value={587}>587 (STARTTLS)</MenuItem>
                </Field>
              </FormControl>
              <QueueSelect
                selectedQueueIds={values.queueIds}
                onChange={(ids) => setFieldValue("queueIds", ids)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} color="secondary" variant="outlined">
                {i18n.t("contactModal.buttons.cancel")}
              </Button>
              <Button
                onClick={() => handleTest(values)}
                color="default"
                variant="outlined"
                disabled={testing || !values.emailAddress}
              >
                {testing ? (
                  <CircularProgress size={20} />
                ) : (
                  i18n.t("connections.zohoModal.test")
                )}
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
                className={classes.btnWrapper}
              >
                {isSubmitting ? (
                  <CircularProgress size={22} color="inherit" />
                ) : isEdit ? (
                  i18n.t("connections.zohoModal.save")
                ) : (
                  i18n.t("connections.zohoModal.connect")
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ZohoMailModal;
